import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

export function usePickReactions(pickId) {
  return useQuery({
    queryKey: ['pickReactions', pickId],
    queryFn: () => api.get(`/social/picks/${pickId}/reactions`),
    enabled: !!pickId,
  })
}

export function useToggleReaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ pickId, reactionType }) =>
      api.post(`/social/picks/${pickId}/reactions`, { reaction_type: reactionType }),
    onSuccess: (_data, { pickId }) => {
      queryClient.invalidateQueries({ queryKey: ['pickReactions', pickId] })
      queryClient.invalidateQueries({ queryKey: ['pickReactionsBatch'] })
    },
  })
}

export function usePickReactionsBatch(pickIds) {
  const key = pickIds?.length ? pickIds.join(',') : ''
  return useQuery({
    queryKey: ['pickReactionsBatch', key],
    // POST not GET — a query string of 100+ UUIDs blows past the URL length cap.
    queryFn: () => api.post('/social/picks/reactions/batch', { pickIds }),
    enabled: !!pickIds?.length,
  })
}

const COMMENT_ROUTES = {
  pick: (id) => `/social/picks/${id}/comments`,
  parlay: (id) => `/social/parlays/${id}/comments`,
  prop: (id) => `/social/props/${id}/comments`,
  streak_event: (id) => `/social/streak-events/${id}/comments`,
  record_history: (id) => `/social/records/${id}/comments`,
  hot_take: (id) => `/social/hot-takes/${id}/comments`,
  hot_take_reminder: (id) => `/social/hot-take-reminders/${id}/comments`,
  head_to_head: (id) => `/social/head-to-head/${id}/comments`,
  futures_pick: (id) => `/social/futures/${id}/comments`,
  league_win: (id) => `/social/league-wins/${id}/comments`,
}

export function useComments(targetType, targetId) {
  return useQuery({
    queryKey: ['comments', targetType, targetId],
    queryFn: () => api.get(COMMENT_ROUTES[targetType](targetId)),
    enabled: !!targetId,
  })
}

export function useAddComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ targetType, targetId, content, parentId }) => {
      const body = { content }
      if (parentId) body.parent_id = parentId
      return api.post(COMMENT_ROUTES[targetType](targetId), body)
    },
    onSuccess: (_data, { targetType, targetId }) => {
      queryClient.invalidateQueries({ queryKey: ['comments', targetType, targetId] })
    },
  })
}

export function useToggleCommentLike() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentId }) => api.post(`/social/comments/${commentId}/like`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] })
    },
  })
}

export function useDeleteComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentId }) => api.delete(`/social/comments/${commentId}`),
    onSuccess: (_data, { targetType, targetId }) => {
      queryClient.invalidateQueries({ queryKey: ['comments', targetType, targetId] })
    },
  })
}

// --- Feed Reactions ---

export function useToggleFeedReaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ targetType, targetId, reactionType }) =>
      api.post('/social/feed/reactions', {
        target_type: targetType,
        target_id: targetId,
        reaction_type: reactionType,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedReactionsBatch'] })
    },
  })
}

export function useFeedReactionsBatch(items) {
  const key = items?.length ? JSON.stringify(items) : ''
  return useQuery({
    queryKey: ['feedReactionsBatch', key],
    // POST not GET — feed items serialized to JSON in a query string hits
    // the URL length cap once the feed has more than ~50 entries.
    queryFn: () => api.post('/social/feed/reactions/batch', { items }),
    enabled: !!items?.length,
  })
}

// Backward-compatible aliases
export function usePickComments(pickId) {
  return useComments('pick', pickId)
}
